import React from 'react';
import { Navigate, Route, Routes, useParams, useLocation, useNavigate } from 'react-router-dom';
import loadable from '@loadable/component';
import {
  ConfigUpdateShow,
  CustomersShown,
  ReportsShown,
  UserIsAuthenticated,
  UserIsNotAuthenticated,
} from './common/services/AuthenticationRules';
import Loader from './common/components/Loader/Loader';
import Page404 from './screens/Page404';
import ForgotPassword from './screens/Login/components/ForgotPassword';
import ResetPassword from './screens/Login/components/ResetPassword';

const Home = loadable(() => import(/* webpackChunkName: "HomePage" */ './screens/HomePage'), {
  fallback: <Loader />,
});

export const Login = loadable(() => import(/* webpackChunkName: "LoginPage" */ './screens/Login'), {
  fallback: <Loader />,
});

const Dashboard = loadable(() => import(/* webpackChunkName: "Dashboard" */ './screens/Dashboard'), {
  fallback: <Loader />,
});

const ChargerDetails = loadable(() => import(/* webpackChunkName: "ChargerDetails" */ './screens/ChargerDetails'), {
  fallback: <Loader />,
});

const ChargersList = loadable(() => import(/* webpackChunkName: "ChargersList" */ './screens/ChargersList'), {
  fallback: <Loader />,
});

const MultiFwUpdate = loadable(() => import(/* webpackChunkName: "MultiFwUpdate" */ './screens/MultiFwUpdate'), {
  fallback: <Loader />,
});

const Profile = loadable(() => import(/* webpackChunkName: "Profile" */ './screens/Profile'), {
  fallback: <Loader />,
});

const ConfigUpdate = loadable(() => import(/* webpackChunkName: "ConfigUpdate" */ './screens/ConfigUpdate'), {
  fallback: <Loader />,
});

const AdminTools = loadable(() => import(/* webpackChunkName: "AdminTools" */ './screens/AdminTools'), {
  fallback: <Loader />,
});

const Reports = loadable(() => import(/* webpackChunkName: "Reports" */ './screens/Reports/Reports'), {
  fallback: <Loader />,
});

const PlazaList = loadable(() => import(/* webpackChunkName: "PlazaList" */ './screens/PlazaList'), {
  fallback: <Loader />,
});

const PlazaView = loadable(() => import(/* webpackChunkName: "PlazaView" */ './screens/PlazaView'), {
  fallback: <Loader />,
});

const Settings = loadable(() => import(/* webpackChunkName: "SettingsPage" */ './screens/SettingsPage'), {
  fallback: <Loader />,
});

const ChargerDetailsWrapper = () => {
  const { tab, chargerId, actionType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <ChargerDetails
      navigate={navigate}
      location={location}
      match={{
        params: {
          actionType,
          tab,
          chargerId,
        },
      }}
    />
  );
};

export default function () {
  return (
    <Routes>
      <Route path="/index.html" render={() => <Navigate to="/" />} />
      <Route exact path="/" Component={Home} />
      <Route exact path="/forgot-password" Component={ForgotPassword} />
      <Route exact path="/chargers/configuration_update" Component={UserIsAuthenticated(ConfigUpdateShow(ConfigUpdate))} />
      <Route exact path="/reset-password" Component={ResetPassword} />
      <Route hasChildren path="/dashboard/*" Component={Dashboard} />
      <Route path="/chargers/:actionType/:chargerId/:tab?/:dataType?" Component={ChargerDetailsWrapper} />
      <Route exact path="/chargers/chargers_list" Component={ChargersList} />
      <Route hasChildren path="/chargers/multiple_fw_update" Component={MultiFwUpdate} />
      <Route exact path="/plaza_list" Component={PlazaList} />
      <Route hasChildren path="/plaza_list/:plazaId/:tab/:tabValue?" Component={PlazaView} />
      <Route hasChildren path="/plaza_list/create_plaza" Component={PlazaView} />
      <Route path="/admin/*" Component={AdminTools} />
      <Route exact path="/chargers/config_update" component={UserIsAuthenticated(ConfigUpdateShow(ConfigUpdate))} />
      <Route exact path="/profile" Component={Profile} />
      <Route path="/login" Component={UserIsNotAuthenticated(Login)} />
      <Route path="/reports/*" Component={ReportsShown(Reports)} />
      <Route path="/customers" Component={CustomersShown(Reports)} />
      <Route path="/settings" Component={Settings} />
      <Route exact path="/pageNotFound" render={(props) => <Page404 showHomeButton {...props} />} />
      <Route Component={Page404} />
    </Routes>
  );
}
